import { AnimatePresence, motion, MotionProps } from "framer-motion";
import React, { useContext, useMemo, useRef } from "react";
import { useOnEnterClick } from "../../../hooks/keyboard-event-hooks";
import ClientUtils from "./../../../utils/ClientUtils";

import { GlobalContext } from "../../../top-level-contexts";
import { AppLoader } from "../loaders/AppLoader";

type FillType = "fill" | "outline";
type Version = "default" | "light-blue" | "error" | "warning" | "success";
type Variant = `${FillType}-${Version}`;

type NativeButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

type FirstClassSupportProps = {
  className?: string;
  id?: string;
  contentClassName?: string;
  children?: React.ReactNode;
  loading?: boolean | number;
  disabled?: boolean;
  onClick?: NativeButtonProps["onClick"];
  type?: "button" | "submit";
  variant?: Variant;
  requiresNetworkConnection?: boolean;
};

type ButtonProps = Omit<MotionProps, keyof FirstClassSupportProps | "whileTap">;

export type AppButtonProps = FirstClassSupportProps & {
  motionProps?: ButtonProps;
};

export function AppButton(props: AppButtonProps) {
  const { isOnline } = useContext(GlobalContext);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useOnEnterClick({
    ref: buttonRef,
    callback: () => {
      buttonRef.current?.click();
    },
  });

  const failsNetworkCheck = props.requiresNetworkConnection && !isOnline;
  const disabled = props.disabled || failsNetworkCheck;
  const variant = props.variant ?? "fill-default";
  const type = props.type ?? "button";
  const enableTapAnimation = !props.loading && !disabled;

  const variantClass = useMemo(() => {
    switch (variant) {
      case "fill-light-blue":
        return "bg-brand-light-blue text-black border-brand-light-blue hover:bg-brand-light-blue/80 hover:text-white";
      case "fill-error":
        return "bg-red-600 text-white border-red-600 hover:bg-red-600/80 hover:text-white";
      case "fill-warning":
        return "bg-yellow-600 text-white border-yellow-600 hover:bg-yellow-600/80 hover:text-white";
      case "fill-success":
        return "bg-green-600 text-white border-green-600 hover:bg-green-600/80 hover:text-white";
      case "outline-default":
        return "bg-transparent text-button-color border-button-color hover:bg-button-color/10";
      case "outline-light-blue":
        return "bg-transparent text-brand-light-blue border-brand-light-blue hover:bg-brand-light-blue/80 hover:text-white";
      case "outline-error":
        return "bg-transparent text-red-600 bg-slate-300/40 border-red-600 hover:bg-red-600/80 hover:text-white";
      case "outline-warning":
        return "bg-transparent text-yellow-600 border-yellow-600 hover:bg-yellow-600/80 hover:text-white";
      case "outline-success":
        return "bg-transparent text-green-600 border-green-600 hover:bg-green-600/80 hover:text-white";
      case "fill-default":
      default:
        return "bg-button-color text-white border-button-color hover:bg-button-color/90";
    }
  }, [variant]);

  return (
    <motion.button
      className={ClientUtils.twClassNames(
        "relative flex items-center justify-center rounded border font-semibold disabled:pointer-events-none disabled:opacity-60",
        disabled && "pointer-events-none opacity-60",
        variantClass,
        props.className
      )}
      id={props.id}
      onClick={props.onClick}
      disabled={disabled}
      type={type}
      ref={buttonRef}
      whileTap={{
        scale: enableTapAnimation ? 0.99 : 1,
      }}
      {...props.motionProps}
    >
      <span
        className={ClientUtils.twClassNames(
          "flex items-center justify-center gap-2 px-4 py-2 transition-all",
          !!props.loading && "opacity-40",
          props.contentClassName
        )}
      >
        {props.children}
      </span>
      <AnimatePresence>
        {!!props.loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-white/50"
          >
            <AppLoader className="h-5 max-h-full w-5" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
